// =====================================================================
// My Agency V2 — toolbar & form inputs (ARTBOARD-SPEC §4 CMP-SEARCH,
// CMP-SEG, CMP-INPUT, CMP-PICKROW). Purely presentational — value,
// onChange and all behavior come from existing platform logic (Rule 6/8;
// existing search implementations are kept, only styled — Rule 11 note
// in FUNCTION-INVENTORY §9).
// =====================================================================
import React from "react";
import { SearchIcon, CloseIcon } from "./icons";
import { RadioDot } from "./glyphs";

// ── CMP-SEARCH — search input ────────────────────────────────────────
// Focus styling via focus-within (border 1.5px #2283FF + ring, icon
// darkens). width: 280 (Agents) / 230 (Players) / "100%" (modal/mobile).
export const SearchInput = ({
  value,
  onChange,
  onClear,
  placeholder,
  width = 280,
  mobile = false,
  iconSize = mobile ? 15 : 15,
  // Clear-chip X size: 11 default (tab search), 10 in modals (board 4b).
  clearIconSize = 11,
  clearTitle,
  inputProps = {},
  style,
  ...rest
}) => (
  <div
    className={[
      "flex items-center gap-2 bg-white border border-[#DCE3EC]",
      "focus-within:border-[#2283FF] focus-within:border-[1.5px] focus-within:shadow-[0_0_0_3px_rgba(34,131,255,.12)]",
      "[&:focus-within_svg]:stroke-[#64748B]",
      mobile ? "rounded-[10px] px-3 py-2.5" : "rounded-[9px] px-3 py-2",
    ].join(" ")}
    style={{ width, maxWidth: "100%", ...style }}
    {...rest}
  >
    <SearchIcon size={iconSize} style={{ flex: "none" }} />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      className={`flex-1 min-w-0 outline-none bg-transparent text-[#0F172A] placeholder:text-[#94A3B8] ${
        mobile ? "text-sm" : "text-[13.5px]"
      }`}
      {...inputProps}
    />
    {onClear && value ? (
      <button
        type="button"
        onClick={() => onClear()}
        title={clearTitle}
        aria-label={clearTitle}
        className="flex-none border-0 bg-[#EAEFF5] rounded-full p-[3px] cursor-pointer hover:bg-[#DCE3EC]"
      >
        <CloseIcon size={clearIconSize} strokeWidth={2.6} />
      </button>
    ) : null}
  </div>
);

// ── CMP-SEG — segmented filter control ───────────────────────────────
// items: [{ key, label, count }]. `dimmed` = the board-1b state while a
// search query is active (opacity .55). `modal` = 4b two-path switch.
export const Segmented = ({ items = [], activeKey, onChange, dimmed = false, modal = false, style, ...rest }) => (
  <div
    className="flex gap-[3px] bg-[#E7EDF4] rounded-[10px] p-[3px]"
    style={{ opacity: dimmed ? 0.55 : 1, ...(modal ? { marginTop: 14 } : null), ...style }}
    {...rest}
  >
    {items.map((it) => {
      const active = it.key === activeKey;
      return (
        <button
          key={it.key}
          type="button"
          onClick={() => onChange && onChange(it.key)}
          className={[
            modal
              ? "flex-1 text-center px-2.5 py-[7px] text-[12.5px]"
              : "flex-none whitespace-nowrap px-[13px] py-1.5 text-[13px]",
            "rounded-lg cursor-pointer border-0",
            active
              ? "font-semibold text-[#0F172A] bg-white shadow-[0_1px_2px_rgba(15,23,42,.08)]"
              : "font-medium text-[#64748B] bg-transparent hover:text-[#0F172A]",
          ].join(" ")}
        >
          {it.label}
          {it.count != null && (
            <span className={active ? "text-[#64748B] font-medium ml-1" : "font-medium ml-1"}>
              {it.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// ── CMP-INPUT — labeled form field wrapper ───────────────────────────
// state: "default" | "valid" | "error". Renders label (+ required /
// optional marks), the field row (children = the actual input element,
// styled bare), and an error message. Existing validation logic stays at
// the call site.
export const FieldLabel = ({ required = false, optionalLabel, children }) => (
  <label className="text-[12.5px] font-semibold text-[#334155]">
    {children}
    {required && <span style={{ color: "#DC2626" }}> *</span>}
    {optionalLabel && (
      <span style={{ fontWeight: 400, color: "#94A3B8" }}> ({optionalLabel})</span>
    )}
  </label>
);

export const FieldShell = ({ state = "default", mobile = false, style, children, ...rest }) => {
  const border =
    state === "error"
      ? { border: "1.5px solid #DC2626", boxShadow: "0 0 0 3px rgba(220,38,38,.1)" }
      : state === "valid"
      ? { border: "1px solid #86D3A5" }
      : { border: "1px solid #DCE3EC" };
  return (
    <div
      className={[
        "flex items-center gap-2 bg-white",
        state === "default"
          ? "focus-within:!border-[#2283FF] focus-within:!border-[1.5px] focus-within:shadow-[0_0_0_3px_rgba(34,131,255,.12)]"
          : "",
        mobile ? "rounded-[10px] px-3 py-[11px]" : "rounded-[9px] px-3 py-[9px]",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ ...border, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
};

export const FieldError = ({ children, ...rest }) => (
  <div className="text-xs text-[#DC2626]" {...rest}>
    {children}
  </div>
);

export const Field = ({ label, required, optionalLabel, state, error, mobile, shellProps = {}, style, children, ...rest }) => (
  <div className="flex flex-col gap-1.5 min-w-0" style={style} {...rest}>
    {label != null && (
      <FieldLabel required={required} optionalLabel={optionalLabel}>
        {label}
      </FieldLabel>
    )}
    <FieldShell state={state} mobile={mobile} {...shellProps}>
      {children}
    </FieldShell>
    {error ? <FieldError>{error}</FieldError> : null}
  </div>
);

// Bare input element for use inside FieldShell.
export const FieldInput = ({ mobile = false, className = "", ...rest }) => (
  <input
    className={[
      "flex-1 min-w-0 outline-none bg-transparent text-[#0F172A] placeholder:text-[#94A3B8]",
      mobile ? "text-[14.5px]" : "text-[13.5px]",
      className,
    ].join(" ")}
    {...rest}
  />
);

// ── CMP-PICKROW — searchable picker result list ──────────────────────
// Container + row. Rows: radio dot (17px/5.5) → avatar node → name/sub
// column → right meta. Selected: bg #F5F9FF + 3px brand left border.
export const PickList = ({ style, children, ...rest }) => (
  <div
    style={{
      border: "1px solid #E3E9F1",
      borderRadius: 11,
      overflow: "hidden",
      marginTop: 10,
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

export const PickRow = ({
  selected = false,
  first = false,
  avatar,
  name,
  subline,
  rightMeta,
  mobile = false,
  showRadio = true,
  style,
  ...rest
}) => (
  <div
    className={selected ? "bg-[#F5F9FF]" : "hover:bg-[#F8FAFC]"}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 11,
      padding: mobile ? "12px 13px" : "11px 13px",
      cursor: "pointer",
      borderTop: first ? "none" : "1px solid #F0F3F8",
      borderLeft: selected ? "3px solid #2283FF" : "3px solid transparent",
      ...style,
    }}
    {...rest}
  >
    {showRadio && <RadioDot selected={selected} size={17} borderWidth={5.5} />}
    {avatar}
    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontSize: mobile ? 14 : 13.5,
          fontWeight: 600,
          color: "#0F172A",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </span>
      {subline}
    </span>
    {rightMeta != null && (
      <span style={{ whiteSpace: "nowrap", fontSize: 12.5, color: "#64748B" }}>{rightMeta}</span>
    )}
  </div>
);
